import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Crown, Download, Share2, Copy, MessageCircle, Home } from "lucide-react";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

export default function Certificato() {
  const { code } = useParams();
  const [cert, setCert] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSeo({
    title: cert
      ? `Certificato Founding Member N°${String(cert.founding_number).padStart(4, "0")} — SuiteCrowd`
      : "Certificato Founding Member — SuiteCrowd",
    description: "Certificato ufficiale Founding Member di SuiteCrowd — il club privato di co-finanziamento turistico.",
  });

  useEffect(() => {
    if (!code) return;
    api
      .get(`/certificato/${code}`)
      .then((r) => setCert(r.data))
      .catch(() => setNotFound(true));
  }, [code]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center space-y-4 max-w-sm">
          <div className="sc-overline">404</div>
          <h1 className="font-display font-light text-3xl">Certificato non trovato</h1>
          <p className="text-sm text-neutral-500">
            Il codice inserito non corrisponde ad alcun Founding Member.
          </p>
          <Link to="/" className="sc-btn-primary inline-flex items-center gap-2">
            <Home size={14} /> Home
          </Link>
        </div>
      </div>
    );
  }

  if (!cert) {
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-500 text-sm tracking-wider">
        Verifica certificato...
      </div>
    );
  }

  const url = `${window.location.origin}/certificato/${cert.referral_code}`;
  const shareText = `Sono Founding Member N°${String(cert.founding_number).padStart(4, "0")} di SuiteCrowd — il club privato di co-finanziamento turistico. ${url}`;

  const copy = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link certificato copiato");
  };
  const whatsapp = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  const nativeShare = () => {
    if (navigator.share) navigator.share({ title: "Founding Member SuiteCrowd", text: shareText, url }).catch(() => {});
    else copy();
  };
  const printCert = () => window.print();

  const issued = cert.issued_at ? new Date(cert.issued_at) : null;

  return (
    <div className="min-h-screen bg-[#F7F7F8] py-16 px-6 md:px-12 print:bg-white print:py-0 print:px-0">
      <div className="max-w-3xl mx-auto">
        {/* Actions */}
        <div className="flex justify-between items-center mb-8 print:hidden">
          <Link to="/" className="text-[11px] tracking-[0.24em] uppercase text-neutral-500 hover:text-[#121212]">
            ← Home
          </Link>
          <div className="flex gap-2">
            <button data-testid="cert-copy" onClick={copy} className="sc-btn-ghost text-[11px] inline-flex items-center gap-2">
              <Copy size={12} /> Copia link
            </button>
            <button data-testid="cert-whatsapp" onClick={whatsapp} className="sc-btn-ghost text-[11px] inline-flex items-center gap-2">
              <MessageCircle size={12} /> WhatsApp
            </button>
            <button data-testid="cert-share" onClick={nativeShare} className="sc-btn-ghost text-[11px] inline-flex items-center gap-2">
              <Share2 size={12} /> Condividi
            </button>
            <button data-testid="cert-print" onClick={printCert} className="sc-btn-primary text-[11px] inline-flex items-center gap-2">
              <Download size={12} /> Stampa PDF
            </button>
          </div>
        </div>

        {/* Certificate */}
        <div
          data-testid="certificate"
          className="bg-white border border-[#0B192C] p-12 md:p-20 relative overflow-hidden"
          style={{ boxShadow: "0 30px 60px -20px rgba(0,0,0,0.15)" }}
        >
          {/* Corners */}
          <div className="absolute top-6 left-6 w-8 h-8 border-t border-l border-[#0B192C]"></div>
          <div className="absolute top-6 right-6 w-8 h-8 border-t border-r border-[#0B192C]"></div>
          <div className="absolute bottom-6 left-6 w-8 h-8 border-b border-l border-[#0B192C]"></div>
          <div className="absolute bottom-6 right-6 w-8 h-8 border-b border-r border-[#0B192C]"></div>

          <div className="text-center space-y-8">
            <div>
              <div
                className="font-display font-light text-3xl"
                style={{ color: "#0B192C", letterSpacing: "0.24em" }}
              >
                SUITE<span style={{ fontWeight: 500 }}>CROWD</span>
              </div>
              <div className="sc-overline mt-2">Certificato Ufficiale</div>
            </div>

            <div className="border-t border-b border-[#E5E5E5] py-8 space-y-3">
              <div className="sc-overline" style={{ color: "#0B192C" }}>
                Founding Member
              </div>
              <div className="font-display font-extralight text-6xl md:text-8xl text-[#0B192C]" style={{ letterSpacing: "-0.03em" }}>
                N° {String(cert.founding_number).padStart(4, "0")}
              </div>
              <div className="font-mono text-sm text-neutral-700 pt-2">
                {cert.email_masked}
              </div>
            </div>

            <p className="text-[14px] text-neutral-600 leading-relaxed max-w-lg mx-auto italic">
              Con il proprio contributo simbolico ha reso possibile il progetto
              SuiteCrowd, il club privato di co-finanziamento turistico. Il tier
              Platino è garantito a vita.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-6 text-center">
              <div>
                <div className="sc-overline mb-1">Data</div>
                <div className="text-sm text-[#121212]">
                  {issued ? issued.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </div>
              </div>
              <div>
                <div className="sc-overline mb-1">Posizione</div>
                <div className="text-sm text-[#121212]">#{cert.position}</div>
              </div>
              <div>
                <div className="sc-overline mb-1">Codice</div>
                <div className="text-sm font-mono text-[#121212]">{cert.referral_code}</div>
              </div>
            </div>

            <div className="pt-6 flex justify-center">
              <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#0B192C]">
                <Crown size={12} /> Tier Platino · A vita
              </div>
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] text-neutral-400 mt-8 tracking-wider print:hidden">
          Verificabile in ogni momento su suitecrowd.com/certificato/{cert.referral_code}
        </p>
      </div>
    </div>
  );
}
